<script lang="ts" setup>
import { computed, ref } from "vue";
import BaseChart, {
  type ChartData,
  type ChartOptions,
} from "@/views/shared/components/BaseChart.vue";
import DashboardHeader from "@/components/DashboardHeader.vue";
import { useDashboardSummary } from "@/queries/dashboard";
import { paletteAt } from "./chartPalette";

// Executions per workflow over the selected range. Doughnut of run volume — the busiest workflows,
// with the long tail folded into "Other" so the slices stay legible.
const range = ref("auto");
const { summary, summaryLoading, summaryError } = useDashboardSummary(range);

const MAX_SLICES = 7;

const slices = computed(() => {
  const sorted = [...summary.value.workflows].sort((a, b) => b.total - a.total);
  const top = sorted.slice(0, MAX_SLICES).map((w) => ({ label: w.name, value: w.total }));
  const rest = sorted.slice(MAX_SLICES);
  if (rest.length > 0) {
    top.push({ label: "Other", value: rest.reduce((sum, w) => sum + w.total, 0) });
  }
  return top.filter((s) => s.value > 0);
});

const hasData = computed(() => slices.value.length > 0);

const chartData = computed<ChartData>(() => ({
  labels: slices.value.map((s) => s.label),
  datasets: [
    {
      label: "Workflow Executions",
      data: slices.value.map((s) => s.value),
      backgroundColor: slices.value.map((_, i) => paletteAt(i).bg),
      borderColor: slices.value.map((_, i) => paletteAt(i).border),
      borderWidth: 1,
    },
  ],
}));

const chartOptions = computed<ChartOptions>(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: true, position: "bottom", labels: { padding: 20 } },
    title: { display: false },
  },
  layout: { padding: { bottom: 10 } },
}));
</script>

<template>
  <DashboardHeader :title="'Workflow Traffic'" />
  <div class="col-xxl-12">
    <div class="row">
      <div class="col">
        <div style="height: 40vh; position: relative">
          <div class="my-4" style="height: 300px">
            <div v-if="summaryLoading && !hasData" class="text-muted py-5 text-center">
              Loading…
            </div>
            <div v-else-if="summaryError" class="text-danger py-5 text-center">
              Couldn't load workflow traffic.
            </div>
            <div v-else-if="!hasData" class="text-muted py-5 text-center">
              No executions yet for this range.
            </div>
            <BaseChart
              v-else
              type="doughnut"
              :data="chartData"
              :options="chartOptions"
              :height="300"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss"></style>
